import { useState, useEffect, useCallback } from 'react';

interface User {
  id: string;
  email: string;
  name: string;
  role: 'superadmin' | 'admin' | 'user' | 'merchant';
  tenantId?: string;
  apiKey?: string;
  permissions?: string[];
}

interface UseAuthReturn {
  user: User | null;
  token: string | null;
  login: (email: string, password: string) => Promise<boolean>;
  logout: () => void;
  loading: boolean;
  hasPermission: (permission: string) => boolean;
  hasRole: (role: string) => boolean;
}

const PERMISSION_MAP: Record<string, string[]> = {
  'superadmin': ['*', 'dashboard', 'payments', 'ecommerce', 'tenants', 'analytics', 'reports', 'settings', 'security', 'monitor', 'api', 'inventory', 'marketing', 'support'],
  'admin': ['dashboard', 'payments', 'ecommerce', 'tenants', 'analytics', 'reports', 'settings', 'support'],
  'merchant': ['dashboard', 'payments', 'ecommerce', 'products', 'orders', 'pos', 'inventory'],
  'user': ['dashboard', 'payments', 'affiliate']
};

export function useAuth(): UseAuthReturn {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  // Check for existing session on mount
  useEffect(() => {
    const storedToken = localStorage.getItem('auth_token');
    const storedUser = localStorage.getItem('auth_user');
    
    if (storedToken && storedUser) {
      setToken(storedToken);
      try {
        setUser(JSON.parse(storedUser));
      } catch (e) {
        localStorage.removeItem('auth_user');
      }
    }
    setLoading(false);
  }, []);

  const hasPermission = useCallback((permission: string): boolean => {
    if (!user) return false;
    
    // Superadmin has all permissions
    if (user.role === 'superadmin') return true;
    if (user.permissions && user.permissions.includes('*')) return true;
    
    // Check user's role-based permissions
    const rolePermissions = PERMISSION_MAP[user.role] || [];
    if (rolePermissions.includes(permission)) return true;
    
    // Check custom permissions
    return user.permissions?.includes(permission) || false;
  }, [user]);

  const hasRole = useCallback((role: string): boolean => {
    if (!user) return false;
    
    // Superadmin matches all roles
    if (user.role === 'superadmin') return true;
    
    return user.role === role;
  }, [user]);

  const login = useCallback(async (email: string, password: string): Promise<boolean> => {
    try {
      // Try to authenticate with the API
      const response = await fetch('/api/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password })
      });

      if (response.ok) {
        const data = await response.json();
        const authToken = data.token || data.apiKey || data.access_token;
        const userData: User = {
          id: data.userId || data.id || '1',
          email: email,
          name: data.name || data.businessName || email,
          role: data.role || 'admin',
          tenantId: data.tenantId,
          apiKey: data.apiKey,
          permissions: data.permissions
        };
        
        localStorage.setItem('auth_token', authToken);
        localStorage.setItem('auth_user', JSON.stringify(userData));
        setToken(authToken);
        setUser(userData);
        return true;
      }
      
      // Fallback: demo credentials
      if (email === 'admin@edgecommerce.com' && password === 'admin123') {
        const demoToken = 'admin_token_' + Date.now();
        const demoUser: User = {
          id: 'tenant_admin_001',
          email,
          name: 'Super Admin',
          role: 'superadmin',
          tenantId: 'tenant_admin_001',
          apiKey: 'pk_admin_master_key',
          permissions: ['*']
        };
        localStorage.setItem('auth_token', demoToken);
        localStorage.setItem('auth_user', JSON.stringify(demoUser));
        setToken(demoToken);
        setUser(demoUser);
        return true;
      }
      
      // Tenant login via API key
      if (password.startsWith('pk_')) {
        const verifyResponse = await fetch(`/api/tenant/verify?apiKey=${encodeURIComponent(password)}`);
        if (verifyResponse.ok) {
          const verifyData = await verifyResponse.json();
          if (verifyData.success) {
            const tenantToken = 'tenant_token_' + Date.now();
            const tenantUser: User = {
              id: verifyData.data.tenantId,
              email: email,
              name: verifyData.data.name,
              role: 'merchant',
              tenantId: verifyData.data.tenantId,
              apiKey: password,
              permissions: ['dashboard', 'payments', 'ecommerce', 'products', 'orders']
            };
            localStorage.setItem('auth_token', tenantToken);
            localStorage.setItem('auth_user', JSON.stringify(tenantUser));
            setToken(tenantToken);
            setUser(tenantUser);
            return true;
          }
        }
      }
      
      return false;
    } catch (error) {
      console.error('Login failed:', error);
      return false;
    }
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem('auth_token');
    localStorage.removeItem('auth_user');
    setToken(null);
    setUser(null);
  }, []);

  return {
    user,
    token,
    login,
    logout,
    loading,
    hasPermission,
    hasRole
  };
}

